// src/components/forms/RenderDefaultAnalytics.js
import React, { useState, useEffect } from 'react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import DataTrend from '../svgIcons/dataTrend';
import { useNavigate } from 'react-router-dom';

import API_CONFIG from '../../config/api';
import { VIZ_URL_CONFIG } from '../../config/api'

const BASE_URL = API_CONFIG.BASE_URL;
const VIZ_BASE_URL = VIZ_URL_CONFIG.BASE_URL;
/**
 * DisplayPlots component is responsible for fetching and rendering analytics plots
 * based on the provided resource ID and fields.
 * It manages its own loading and error states for plot availability.
 */

const DisplayPlots = ({ resourceId, resourceFields }) => {
	const [plotUrls, setPlotUrls] = useState([]);
	const [plotsLoading, setPlotsLoading] = useState(true);
	const [plotsError, setPlotsError] = useState(null);
	const [loaded, setLoaded] = React.useState(false);
	
	// useEffect to perform the asynchronous fetching of plot availability
	useEffect(() => {
		const fetchPlotAvailability = async () => {
			// Do not proceed if essential props are missing
			if (!resourceId || !resourceFields || resourceFields.length === 0) {
				setPlotUrls([]);
				setPlotsLoading(false);
				return;
			}

			setPlotsLoading(true);
			setPlotsError(null);
			const validPlotUrls = [];

			// Iterate over each field to check for available plots
			for (const questionName of resourceFields) {
				try {

					const dashUrl = `${VIZ_BASE_URL}/dash/${resourceId}/default/${questionName}`;

					// Test if the plot exists by making a request
					const plotResponse = await fetch(dashUrl);

					if (plotResponse.ok) {
						const html = await plotResponse.text();
						// Check if the response HTML does not indicate a 404 error from Dash
						if (!html.includes('data-status="404"') && !html.includes('No matching views found')) {
							validPlotUrls.push({
								questionName,
								url: dashUrl
							});
						}
					} else {
						console.warn(`Failed to fetch plot for question ${questionName}: HTTP status ${plotResponse.status}`);
					}
				} catch (err) {
					console.warn(`Failed to fetch plot for question ${questionName}:`, err);
					// Continue with other questions even if one fails
				}
			}
			setPlotUrls(validPlotUrls);
			setPlotsLoading(false);
		};

		fetchPlotAvailability();
	}, [resourceId, resourceFields]); // Dependencies: Re-run effect if resourceId or resourceFields change

	// Render loading state for plots
	if (plotsLoading) {
		return (
			<div className="p-8">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{[1, 2].map((i) => ( // Display a few shimmer placeholders for plots
						<div key={i} className="bg-gray-100 rounded-lg animate-pulse" style={{ height: '400px' }}>
							<div className="p-4">
								<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
								<div className="h-3 bg-gray-200 rounded w-1/2"></div>
							</div>
						</div>
					))}
				</div>
				<style>{`
					@keyframes shimmer {
						0% { background-position: -200% 0; }
						100% { background-position: 200% 0; }
					}
				`}</style>
			</div>
		);
	}

	// Render error state for plots (if any occurred during fetching plot URLs)
	if (plotsError) {
		return (
			<div className="p-8">
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<div className="flex">
						<div className="flex-shrink-0">
							<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
							</svg>
						</div>
						<div className="ml-3">
							<h3 className="text-sm font-medium text-red-800">
								Plot Loading Error
							</h3>
							<p className="mt-1 text-sm text-red-700">
								{plotsError}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// Render message if no plots are available
	if (plotUrls.length === 0) {
		return (
			<div className="p-8 text-center text-gray-500">
				<div className="flex flex-col items-center">
					<DataTrend className="w-16 h-16 text-gray-300 mb-4" />
					<h3 className="text-lg font-medium text-gray-700 mb-2">No Analytics Available</h3>
					<p className="text-gray-500">
						{resourceFields.length === 0
							? "No fields found for this survey"
							: `Found ${resourceFields.length} field(s), but no visualizable data available`}
					</p>
				</div>
			</div>
		);
	}

	// Render the plots
	return (
		<div className="p-6">
			<div className="mb-4">
				<h2 className="text-lg font-semibold text-gray-900">
					Survey Analytics ({plotUrls.length} visualization{plotUrls.length !== 1 ? 's' : ''})
				</h2>
				<p className="text-sm text-gray-600">
					Resource ID: {resourceId}
				</p>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{plotUrls.map(({ questionName, url }) => (
					<div key={questionName} className="bg-gray-50 rounded-lg border overflow-hidden">
						<div className="bg-white px-4 py-3 border-b">
							<h3 className="text-md font-medium text-gray-900 capitalize">
								{questionName.replace(/_/g, ' ')}
							</h3>
						</div>
						<div className="relative">
							<iframe
								src={url}
								className="w-full border-0"
								style={{ height: '400px' }}
								title={`Analytics for ${questionName}`}
								onLoad={() => setLoaded(true)}
								onError={(e) => {
									console.error(`Failed to load iframe for ${questionName}:`, e);
								}}
							/>
							{!loaded && (
								<div
									className="absolute inset-0 flex items-center justify-center"
									style={{
										background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
										backgroundSize: '200% 100%',
										animation: 'shimmer 1.5s ease-in-out infinite'
									}}
								>
									<div className="text-gray-500 text-sm">Loading visualization...</div>
								</div>
							)}
						</div>
					</div>
				))}
			</div>

			{/* Shimmer animation CSS for the loading overlay */}
			<style>{`
				@keyframes shimmer {
					0% { background-position: -200% 0; }
					100% { background-position: 200% 0; }
				}
			`}</style>
		</div>
	);
};

/**
 * RenderDefaultAnalytics component is the main container that fetches initial survey data
 * and then passes it to DisplayPlots for rendering.
 */
const RenderDefaultAnalytics = ({ surveyId }) => {
	const [resourceId, setResourceId] = useState(null);
	const [fields, setFields] = useState([]);
	const [analyticsLoading, setAnalyticsLoading] = useState(true);
	const [analyticsError, setAnalyticsError] = useState(null);
	const { authenticatedFetch } = useAuthenticatedApi();
	const navigate = useNavigate();

	useEffect(() => {
		const fetchAnalyticsData = async () => {
			if (!surveyId) {
				setAnalyticsLoading(false);
				return;
			}

			setAnalyticsLoading(true);
			setAnalyticsError(null);

			try {
				// Step 1: Resolve resource_id and fields for the given surveyId
				const resolveEndpoint = `${BASE_URL}/active_surveys/dash/resolve-resource-id/${surveyId}`;
				const result = await authenticatedFetch(resolveEndpoint, {}, "GET");

				if (!result.ok) {
					// Handle specific HTTP error statuses
					if (result.status === 404) {
						navigate('/404', { state: { message: `Survey with ID "${surveyId}" not found.` } });
						return; // Important: return to stop further execution in this effect
					}
					if (result.status === 401) {
						setAnalyticsError("You are not authorized to access this survey or you are not authenticated.");
						setAnalyticsLoading(false);
						return;
					}

					// Generic error handling for other non-2xx responses
					let errorMessage = `Failed to fetch survey: Server responded with status ${result.status}.`;
					try {
						const errorBody = await result.json();
						if (errorBody && errorBody.detail) {
							errorMessage = errorBody.detail;
						}
					} catch (jsonError) {
						// If response body isn't JSON, ignore and keep generic error message
					}
					console.error("Analytics API Error:", errorMessage, result);
					setAnalyticsError(errorMessage);
					setAnalyticsLoading(false);
					return;
				}

				const resolveData = await result.json();

				// Check for a 'success' field in the backend response
				if (!resolveData.success) {
					setAnalyticsError(resolveData.message || `Failed to resolve resource for survey_id: ${surveyId}.`);
					setAnalyticsLoading(false);
					return;
				}

				setResourceId(resolveData.resource_id);
				setFields(resolveData.fields || []); // Ensure fields is an array
			} catch (err) {
				console.error("Failed to fetch analytics data:", err);
				setAnalyticsError(err.message || "Failed to load analytics data.");
			} finally {
				setAnalyticsLoading(false);
			}
		};

		fetchAnalyticsData();
	}, [surveyId]);

	// Render loading state for the main analytics data
	if (analyticsLoading) {
		return (
			<div className="p-8">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{[1, 2, 3, 4].map((i) => (
						<div key={i} className="bg-gray-100 rounded-lg animate-pulse" style={{ height: '400px' }}>
							<div className="p-4">
								<div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
								<div className="h-3 bg-gray-200 rounded w-1/2"></div>
							</div>
						</div>
					))}
				</div>
			</div>
		);
	}

	// Render error state for the main analytics data
	if (analyticsError) {
		return (
			<div className="p-8">
				<div className="bg-red-50 border border-red-200 rounded-lg p-4">
					<div className="flex">
						<div className="flex-shrink-0">
							<svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
								<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
							</svg>
						</div>
						<div className="ml-3">
							<h3 className="text-sm font-medium text-red-800">
								Analytics Error
							</h3>
							<p className="mt-1 text-sm text-red-700">
								{analyticsError}
							</p>
						</div>
					</div>
				</div>
			</div>
		);
	}

	// If initial data is loaded without errors, render the DisplayPlots component
	return (
		<DisplayPlots
			resourceId={resourceId}
			resourceFields={fields} // Pass the fetched fields to DisplayPlots
		/>
	);
};

export default RenderDefaultAnalytics;